import { useState, useRef, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { productsAPI, categoriesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { toast } from 'sonner';
import {
    Upload, Download, FileText, CheckCircle2, XCircle,
    Loader2, AlertCircle, Package
} from 'lucide-react';

const REQUIRED_COLS = ['name', 'sku', 'price', 'cost_price', 'stock_quantity'];
const OPTIONAL_COLS = ['description', 'category_name', 'reorder_point'];

const TEMPLATE_ROWS = [
    'name,sku,price,cost_price,stock_quantity,description,category_name,reorder_point',
    'Espresso Shot,ESP-001,3.50,0.80,100,Double espresso,Beverages,20',
    'Blueberry Muffin,MFN-001,2.75,0.60,50,Fresh baked daily,Pastries,10',
];

function downloadTemplate() {
    const csv = TEMPLATE_ROWS.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mezgebpos_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
}

function parseCSV(text) {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return { error: 'File must have a header row and at least one data row.' };

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));

    const missing = REQUIRED_COLS.filter(c => !headers.includes(c));
    if (missing.length > 0) {
        return { error: `Missing required columns: ${missing.join(', ')}` };
    }

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        // Handle quoted fields
        const values = [];
        let cur = '';
        let inQuote = false;
        for (const ch of lines[i]) {
            if (ch === '"') { inQuote = !inQuote; }
            else if (ch === ',' && !inQuote) { values.push(cur.trim()); cur = ''; }
            else { cur += ch; }
        }
        values.push(cur.trim());

        const row = {};
        headers.forEach((h, idx) => { row[h] = values[idx] || ''; });

        const errors = [];
        if (!row.name) errors.push('name required');
        if (!row.sku) errors.push('sku required');
        const price = parseFloat(row.price);
        if (isNaN(price) || price < 0) errors.push('invalid price');
        const cost = parseFloat(row.cost_price);
        if (isNaN(cost) || cost < 0) errors.push('invalid cost_price');
        const qty = parseInt(row.stock_quantity, 10);
        if (isNaN(qty) || qty < 0) errors.push('invalid stock_quantity');

        rows.push({
            _line: i + 1,
            _errors: errors,
            name: row.name,
            sku: row.sku,
            price,
            cost_price: cost,
            stock_quantity: qty,
            description: row.description || '',
            category_name: row.category_name || '',
            reorder_point: parseInt(row.reorder_point, 10) || 0,
        });
    }

    return { rows };
}

export default function InventoryImport() {
    const fileRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [preview, setPreview] = useState(null);
    const [parseError, setParseError] = useState('');
    const [importing, setImporting] = useState(false);
    const [results, setResults] = useState(null);

    const handleFile = useCallback((file) => {
        if (!file) return;
        if (!file.name.endsWith('.csv')) {
            setParseError('Only .csv files are supported.'); return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            const { rows, error } = parseCSV(e.target.result);
            if (error) { setParseError(error); setPreview(null); return; }
            setParseError('');
            setPreview(rows);
            setResults(null);
        };
        reader.readAsText(file);
    }, []);

    const onDrop = useCallback((e) => {
        e.preventDefault();
        setDragging(false);
        handleFile(e.dataTransfer.files[0]);
    }, [handleFile]);

    const validRows = preview?.filter(r => r._errors.length === 0) || [];
    const invalidRows = preview?.filter(r => r._errors.length > 0) || [];

    const handleImport = async () => {
        if (!validRows.length) return;
        setImporting(true);

        // Pre-fetch categories once and build a name→id map
        let categoryMap = {};
        try {
            const catRes = await categoriesAPI.getAll();
            (catRes.data || []).forEach(c => { categoryMap[c.name.toLowerCase()] = c.id; });
        } catch { /* proceed without category mapping */ }

        const succeeded = [];
        const failed = [];

        for (const row of validRows) {
            // Resolve or create category
            let category_id = '';
            if (row.category_name) {
                const key = row.category_name.toLowerCase();
                if (categoryMap[key]) {
                    category_id = categoryMap[key];
                } else {
                    try {
                        const newCat = await categoriesAPI.create({ name: row.category_name });
                        category_id = newCat.data.id;
                        categoryMap[key] = category_id;
                    } catch { /* skip category */ }
                }
            }

            try {
                await productsAPI.create({
                    name: row.name,
                    sku: row.sku,
                    price: row.price,
                    cost_price: row.cost_price,
                    stock_quantity: row.stock_quantity,
                    description: row.description,
                    category_id,
                    reorder_point: row.reorder_point,
                });
                succeeded.push(row);
            } catch (err) {
                failed.push({ row, reason: err.response?.data?.detail || 'Unknown error' });
            }
        }

        setResults({ succeeded, failed });
        setImporting(false);
        setPreview(null);

        if (succeeded.length > 0) {
            toast.success(`${succeeded.length} product${succeeded.length !== 1 ? 's' : ''} imported`);
        }
        if (failed.length > 0) {
            toast.error(`${failed.length} product${failed.length !== 1 ? 's' : ''} failed`);
        }
    };

    return (
        <Layout title="Import Products">
            <div className="max-w-4xl space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-semibold">Bulk Import Products</h2>
                        <p className="text-sm text-muted-foreground">
                            Upload a CSV file to add multiple products at once
                        </p>
                    </div>
                    <Button variant="outline" onClick={downloadTemplate}>
                        <Download className="h-4 w-4 mr-2" /> Download Template
                    </Button>
                </div>

                {/* Required columns info */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <FileText className="h-4 w-4" /> CSV Format
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-1">
                        <p>
                            <span className="font-medium text-foreground">Required: </span>
                            {REQUIRED_COLS.map(c => (
                                <Badge key={c} variant="outline" className="mr-1 font-mono text-xs">{c}</Badge>
                            ))}
                        </p>
                        <p>
                            <span className="font-medium text-foreground">Optional: </span>
                            {OPTIONAL_COLS.map(c => (
                                <Badge key={c} variant="outline" className="mr-1 font-mono text-xs text-muted-foreground">{c}</Badge>
                            ))}
                        </p>
                        <p className="text-xs pt-1">
                            Categories are matched by name and created automatically if they don't exist.
                        </p>
                    </CardContent>
                </Card>

                {/* Drop zone */}
                {!preview && !results && (
                    <div
                        className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer
                            ${dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={onDrop}
                        onClick={() => fileRef.current?.click()}
                    >
                        <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                        <p className="font-medium">Drop your CSV here or click to browse</p>
                        <p className="text-sm text-muted-foreground mt-1">Supports .csv files only</p>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".csv"
                            className="hidden"
                            onChange={e => handleFile(e.target.files[0])}
                        />
                    </div>
                )}

                {/* Parse error */}
                {parseError && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive">
                        <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="font-medium">Could not parse file</p>
                            <p className="text-sm">{parseError}</p>
                        </div>
                    </div>
                )}

                {/* Preview table */}
                {preview && (
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Preview — {preview.length} rows</CardTitle>
                                    <CardDescription>
                                        {validRows.length} valid · {invalidRows.length} with errors
                                    </CardDescription>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="outline" onClick={() => setPreview(null)}>
                                        Change File
                                    </Button>
                                    <Button
                                        onClick={handleImport}
                                        disabled={importing || validRows.length === 0}
                                    >
                                        {importing
                                            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing…</>
                                            : <><Package className="h-4 w-4 mr-2" /> Import {validRows.length} Products</>
                                        }
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="max-h-96 overflow-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-8">#</TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead>SKU</TableHead>
                                            <TableHead>Price</TableHead>
                                            <TableHead>Cost</TableHead>
                                            <TableHead>Qty</TableHead>
                                            <TableHead>Category</TableHead>
                                            <TableHead>Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {preview.map((row) => (
                                            <TableRow key={row._line} className={row._errors.length ? 'bg-destructive/5' : ''}>
                                                <TableCell className="text-xs text-muted-foreground">{row._line}</TableCell>
                                                <TableCell className="font-medium">{row.name || '—'}</TableCell>
                                                <TableCell className="font-mono text-xs">{row.sku || '—'}</TableCell>
                                                <TableCell>${isNaN(row.price) ? '—' : row.price.toFixed(2)}</TableCell>
                                                <TableCell>${isNaN(row.cost_price) ? '—' : row.cost_price.toFixed(2)}</TableCell>
                                                <TableCell>{isNaN(row.stock_quantity) ? '—' : row.stock_quantity}</TableCell>
                                                <TableCell className="text-sm text-muted-foreground">{row.category_name || '—'}</TableCell>
                                                <TableCell>
                                                    {row._errors.length === 0 ? (
                                                        <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/20">
                                                            <CheckCircle2 className="h-3 w-3 mr-1" /> Valid
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="text-xs bg-destructive/10 text-destructive border-destructive/20">
                                                            <XCircle className="h-3 w-3 mr-1" /> {row._errors.join(', ')}
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Results */}
                {results && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <Card className="border-green-500/30 bg-green-500/5">
                                <CardContent className="flex items-center gap-4 pt-6">
                                    <CheckCircle2 className="h-8 w-8 text-green-600 flex-shrink-0" />
                                    <div>
                                        <p className="text-2xl font-bold text-green-600">{results.succeeded.length}</p>
                                        <p className="text-sm text-muted-foreground">Products imported</p>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className={results.failed.length > 0 ? 'border-destructive/30 bg-destructive/5' : ''}>
                                <CardContent className="flex items-center gap-4 pt-6">
                                    <XCircle className={`h-8 w-8 flex-shrink-0 ${results.failed.length > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
                                    <div>
                                        <p className={`text-2xl font-bold ${results.failed.length > 0 ? 'text-destructive' : ''}`}>{results.failed.length}</p>
                                        <p className="text-sm text-muted-foreground">Failed</p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {results.failed.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm text-destructive">Failed Rows</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Name</TableHead>
                                                <TableHead>SKU</TableHead>
                                                <TableHead>Reason</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {results.failed.map(({ row, reason }, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{row.name}</TableCell>
                                                    <TableCell className="font-mono text-xs">{row.sku}</TableCell>
                                                    <TableCell className="text-destructive text-sm">{reason}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        )}

                        <Button variant="outline" onClick={() => { setResults(null); setPreview(null); }}>
                            Import Another File
                        </Button>
                    </div>
                )}
            </div>
        </Layout>
    );
}
